import { useCallback, useEffect, useState } from "react";

import {
  applyTheme,
  loadThemeMode,
  nextThemeMode,
  type ResolvedTheme,
  type ThemeMode,
} from "@/theme/theme.ts";

/**
 * The theme preference plus the key it currently resolves to. In "system" mode
 * it re-resolves when the OS flips, so the app follows a scheduled dark-mode
 * switch without a reload.
 */
export function useTheme(): {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  cycle: () => void;
} {
  const [mode, setModeState] = useState<ThemeMode>(loadThemeMode);
  const [resolved, setResolved] = useState<ResolvedTheme>(
    () => (document.documentElement.dataset.theme as ResolvedTheme | undefined) ?? "dark",
  );

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    setResolved(applyTheme(next));
  }, []);

  // Follow the OS only while the preference is "system".
  useEffect(() => {
    setResolved(applyTheme(mode));
    if (mode !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setResolved(applyTheme("system"));
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [mode]);

  const cycle = useCallback(() => {
    setMode(nextThemeMode(mode));
  }, [mode, setMode]);

  return { mode, resolved, setMode, cycle };
}
